import { useState, useEffect, useCallback } from 'react';
import { FaExchangeAlt, FaChevronDown, FaChevronUp, FaSearch, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import * as Sentry from '@sentry/react';

/**
 * AllBoardsManagementPanel Component
 *
 * Admin-only panel that lists ALL boards across the system (regardless of ownership).
 * Allows admins to:
 * - View all boards with their owners and status
 * - Identify "orphan" boards (whose owner is inactive/left)
 * - Transfer ownership of any board to an active user
 *
 * This is embedded in the BoardCreationRulesPage as a collapsible section.
 */
const AllBoardsManagementPanel = () => {
    const { apiFetch } = useAuth();
    const [boards, setBoards] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [transferTarget, setTransferTarget] = useState(null);
    const [newOwnerId, setNewOwnerId] = useState('');
    const [transferring, setTransferring] = useState(false);
    const [filterOrphans, setFilterOrphans] = useState(false);

    /**
     * Fetch all boards from admin endpoint
     */
    const fetchAllBoards = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/api/KanbanBoards/admin/all-boards');
            const data = await res.json();
            if (data.success) {
                setBoards(data.data || []);
            } else {
                toast.error(data.message || 'Failed to load boards');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Failed to load boards');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    /**
     * Fetch active users for the transfer dropdown
     */
    const fetchUsers = useCallback(async () => {
        try {
            const res = await apiFetch('/api/User/all');
            const data = await res.json();
            if (data.success) {
                const activeUsers = (data.data || []).filter(u => u.isActive);
                setUsers(activeUsers);
            }
        } catch (error) {
            Sentry.captureException(error);
        }
    }, [apiFetch]);

    useEffect(() => {
        if (expanded && boards.length === 0) {
            const loadData = async () => {
                await Promise.all([fetchAllBoards(), fetchUsers()]);
            };
            loadData();
        }
    }, [expanded, boards.length, fetchAllBoards, fetchUsers]);

    /**
     * Transfer board ownership to a new user
     */
    const handleTransfer = async () => {
        if (!transferTarget || !newOwnerId) return;

        setTransferring(true);
        try {
            const res = await apiFetch(`/api/KanbanBoards/${transferTarget.boardId}/transfer-ownership`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newOwnerId: parseInt(newOwnerId) })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message || 'Ownership transferred');
                setTransferTarget(null);
                setNewOwnerId('');
                await fetchAllBoards();
            } else {
                toast.error(data.message || 'Transfer failed');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Failed to transfer ownership');
        } finally {
            setTransferring(false);
        }
    };

    /**
     * Filter boards based on search query and orphan filter
     */
    const filteredBoards = boards.filter(b => {
        const matchesSearch = !searchQuery ||
            b.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            b.ownerName?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesOrphan = !filterOrphans || !b.ownerIsActive;
        return matchesSearch && matchesOrphan;
    });

    const orphanCount = boards.filter(b => !b.ownerIsActive).length;

    return (
        <div style={styles.container}>
            {/* Collapsible Header */}
            <button
                style={styles.sectionHeader}
                onClick={() => setExpanded(!expanded)}
            >
                <span style={styles.sectionTitle}>
                    📋 All Boards Management
                    {orphanCount > 0 && (
                        <span style={styles.orphanBadge}>
                            <FaExclamationTriangle size={12} /> {orphanCount} orphan{orphanCount !== 1 ? 's' : ''}
                        </span>
                    )}
                </span>
                {expanded ? <FaChevronUp /> : <FaChevronDown />}
            </button>

            {expanded && (
                <div style={styles.content}>
                    <p style={styles.description}>
                        View and manage all boards across the system. Transfer ownership of orphan boards
                        (boards whose owner has left) to active users.
                    </p>

                    {/* Search and filter controls */}
                    <div style={styles.controls}>
                        <div style={styles.searchBox}>
                            <FaSearch style={styles.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search boards or owners..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={styles.searchInput}
                            />
                        </div>
                        <label style={styles.filterLabel}>
                            <input
                                type="checkbox"
                                checked={filterOrphans}
                                onChange={(e) => setFilterOrphans(e.target.checked)}
                            />
                            Show orphans only ({orphanCount})
                        </label>
                    </div>

                    {loading ? (
                        <p style={styles.loadingText}>Loading all boards...</p>
                    ) : (
                        <div style={styles.tableWrapper}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={{ ...styles.th, textAlign: 'left' }}>Board</th>
                                        <th style={{ ...styles.th, textAlign: 'left' }}>Owner</th>
                                        <th style={{ ...styles.th, textAlign: 'left' }}>Status</th>
                                        <th style={{ ...styles.th, textAlign: 'left' }}>Columns</th>
                                        <th style={{ ...styles.th, textAlign: 'left' }}>Access Grants</th>
                                        <th style={{ ...styles.th, textAlign: 'left' }}>Created</th>
                                        <th style={{ ...styles.th, textAlign: 'left' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredBoards.map(board => (
                                        <tr key={board.boardId} style={!board.ownerIsActive ? styles.orphanRow : {}}>
                                            <td style={styles.td}>
                                                <div style={styles.boardName}>
                                                    {board.color && (
                                                        <span style={{ ...styles.colorDot, backgroundColor: board.color }} />
                                                    )}
                                                    {board.name}
                                                    {!board.isActive && <span style={styles.inactiveBadge}>Archived</span>}
                                                    {board.isPublic && <span style={styles.publicBadge}>Public</span>}
                                                </div>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={!board.ownerIsActive ? styles.inactiveOwner : {}}>
                                                    {board.ownerName}
                                                    {!board.ownerIsActive && (
                                                        <FaExclamationTriangle
                                                            size={12}
                                                            style={{ marginLeft: '4px', color: '#dc2626' }}
                                                            title="Owner is inactive"
                                                        />
                                                    )}
                                                </span>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={board.isActive ? styles.activeBadge : styles.archivedBadge}>
                                                    {board.isActive ? 'Active' : 'Archived'}
                                                </span>
                                            </td>
                                            <td style={styles.td}>{board.columnCount}</td>
                                            <td style={styles.td}>{board.accessGrantCount}</td>
                                            <td style={styles.td}>
                                                {new Date(board.createdDate).toLocaleDateString()}
                                            </td>
                                            <td style={styles.td}>
                                                <button
                                                    style={styles.transferBtn}
                                                    onClick={() => { setTransferTarget(board); setNewOwnerId(''); }}
                                                    title="Transfer ownership"
                                                >
                                                    <FaExchangeAlt size={14} /> Transfer
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredBoards.length === 0 && (
                                        <tr>
                                            <td colSpan={7} style={styles.emptyCell}>
                                                {searchQuery || filterOrphans
                                                    ? 'No boards match the current filter'
                                                    : 'No boards found'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Transfer Ownership Modal */}
                    {transferTarget && (
                        <div style={styles.overlay} onClick={() => setTransferTarget(null)}>
                            <div style={styles.modal} onClick={e => e.stopPropagation()}>
                                <h3 style={styles.modalTitle}>Transfer Board Ownership</h3>
                                <p style={styles.modalText}>
                                    Transfer <strong>&quot;{transferTarget.name}&quot;</strong> from{' '}
                                    <strong>{transferTarget.ownerName}</strong> to:
                                </p>
                                <select
                                    value={newOwnerId}
                                    onChange={(e) => setNewOwnerId(e.target.value)}
                                    style={styles.select}
                                >
                                    <option value="">-- Select new owner --</option>
                                    {users.map(u => (
                                        <option key={u.userID || u.id} value={u.userID || u.id}>
                                            {u.firstName} {u.lastName} ({u.email || u.department || ''})
                                        </option>
                                    ))}
                                </select>
                                <div style={styles.modalActions}>
                                    <button style={styles.btnCancel} onClick={() => setTransferTarget(null)}>
                                        Cancel
                                    </button>
                                    <button
                                        style={styles.btnTransfer}
                                        onClick={handleTransfer}
                                        disabled={!newOwnerId || transferring}
                                    >
                                        {transferring ? 'Transferring...' : 'Transfer Ownership'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * Inline styles for the AllBoardsManagementPanel
 */
const styles = {
    container: {
        marginTop: '24px',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        overflow: 'hidden'
    },
    sectionHeader: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '14px 20px',
        background: '#f9fafb',
        border: 'none',
        cursor: 'pointer',
        fontSize: '1rem',
        fontWeight: '600',
        color: '#374151'
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    orphanBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 8px',
        background: '#fef2f2',
        color: '#dc2626',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: '500'
    },
    content: {
        padding: '16px 20px'
    },
    description: {
        color: '#6b7280',
        fontSize: '0.9rem',
        marginBottom: '16px'
    },
    controls: {
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        marginBottom: '12px',
        flexWrap: 'wrap'
    },
    searchBox: {
        position: 'relative',
        flex: 1,
        minWidth: '200px'
    },
    searchIcon: {
        position: 'absolute',
        left: '10px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#9ca3af'
    },
    searchInput: {
        width: '100%',
        padding: '8px 12px 8px 32px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        fontSize: '0.9rem'
    },
    filterLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '0.85rem',
        color: '#374151',
        cursor: 'pointer',
        whiteSpace: 'nowrap'
    },
    loadingText: {
        color: '#6b7280',
        fontStyle: 'italic'
    },
    tableWrapper: {
        overflowX: 'auto'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '0.85rem'
    },
    th: {
        padding: '10px 12px',
        borderBottom: '2px solid #e5e7eb',
        fontWeight: '600',
        color: '#374151'
    },
    td: {
        padding: '10px 12px',
        borderBottom: '1px solid #f3f4f6'
    },
    orphanRow: {
        background: '#fef2f2'
    },
    boardName: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontWeight: '500'
    },
    colorDot: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        display: 'inline-block'
    },
    inactiveOwner: {
        color: '#dc2626',
        fontWeight: '500'
    },
    activeBadge: {
        padding: '2px 8px',
        background: '#dcfce7',
        color: '#166534',
        borderRadius: '12px',
        fontSize: '0.75rem'
    },
    archivedBadge: {
        padding: '2px 8px',
        background: '#f3f4f6',
        color: '#6b7280',
        borderRadius: '12px',
        fontSize: '0.75rem'
    },
    inactiveBadge: {
        padding: '2px 6px',
        background: '#f3f4f6',
        color: '#6b7280',
        borderRadius: '4px',
        fontSize: '0.7rem',
        marginLeft: '4px'
    },
    publicBadge: {
        padding: '2px 6px',
        background: '#dbeafe',
        color: '#1e40af',
        borderRadius: '4px',
        fontSize: '0.7rem',
        marginLeft: '4px'
    },
    transferBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 10px',
        background: '#eef2ff',
        color: '#4338ca',
        border: '1px solid #c7d2fe',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.8rem',
        fontWeight: '500'
    },
    emptyCell: {
        textAlign: 'center',
        padding: '20px',
        color: '#6b7280',
        fontStyle: 'italic'
    },
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    },
    modal: {
        background: '#fff',
        borderRadius: '10px',
        padding: '24px',
        width: '450px',
        maxWidth: '90vw'
    },
    modalTitle: {
        margin: '0 0 12px',
        fontSize: '1.1rem'
    },
    modalText: {
        color: '#4b5563',
        marginBottom: '16px'
    },
    select: {
        width: '100%',
        padding: '10px 12px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        fontSize: '0.9rem',
        marginBottom: '16px'
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px'
    },
    btnCancel: {
        padding: '8px 16px',
        background: '#f3f4f6',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        cursor: 'pointer'
    },
    btnTransfer: {
        padding: '8px 16px',
        background: '#4338ca',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '500'
    }
};

export default AllBoardsManagementPanel;
